import React, { useEffect, useState } from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import MSelect from '../components/MSelect';
import { toast } from 'react-toastify';
import { DEFAULT_OPTION } from '../utils/Constant';
import { useAppDispatch, useAppSelector } from '../store/redux-hooks';
import {
  ColXsMdType,
  MUILocationFilterType,
  SelectOptionType,
} from '../utils/Types';
import MTextField from '../components/MTextField';
import { listTypes, moduleIds } from '../pages/create-activity/activityHelper';
import {
  getStatusDrops,
  getYearSeasonOptions,
  marketingActivitySelector,
} from '../features/marketingActivitySlice';

import MUILocationFilter from '../components/MUILocationFilter';

interface ISetActivityFilterProps {
  filterValues?: any;
  setFilterValues?: any;
  onReset: () => void;
  listType: string;
  onSubmitFilter: (isValid?: boolean) => void;
  defaultLocationOptions?: any;
}

const SetActivityFilter: React.FunctionComponent<ISetActivityFilterProps> = ({
  setFilterValues,
  filterValues,
  onReset,
  listType,
  onSubmitFilter,
  defaultLocationOptions = {},
}) => {
  const yearOptions = useAppSelector(marketingActivitySelector).yearOptions;
  const seasonOptions = useAppSelector(marketingActivitySelector).seasonOptions;
  const statusOptions = useAppSelector(marketingActivitySelector).statusOptions;
  const dispatch = useAppDispatch();
  const [yearOptionsToCreate, setYearOptionsToCreate] = useState<
    SelectOptionType[]
  >([]);
  useEffect(() => {
    if (yearOptions.length) {
      const options: SelectOptionType[] = [];
      if (listType === listTypes.create) {
        yearOptions.map((year) => {
          if (year.isCurrentYear) {
            options.push({
              value: year.financialYearId,
              label: year.financialYearName,
            });
          }
        });
      } else {
        yearOptions.map((year) => {
          options.push({
            value: year.financialYearId,
            label: year.financialYearName,
          });
        });
      }
      setYearOptionsToCreate(options);
    } else {
      setYearOptionsToCreate([DEFAULT_OPTION]);
    }
  }, [yearOptions, listType]);

  useEffect(() => {
    dispatch(getYearSeasonOptions());
    if (listType === listTypes.review)
      dispatch(getStatusDrops({ module: moduleIds.status }));
  }, []);

  const isCreateMode = listType === listTypes.create;
  const isUpdateMode = listType === listTypes.update;

  const onApply = () => {
    let isValid = true;
    if (isCreateMode) {
      if (!filterValues.year) {
        toast.error('Year is required');
        isValid = false;
      } else if (!filterValues.season) {
        toast.error('Season is required');
        isValid = false;
      } else if (!filterValues.zone) {
        toast.error('Zone is required');
        isValid = false;
      } else if (!filterValues.state) {
        toast.error('State is required');
        isValid = false;
      } else if (!filterValues.district) {
        toast.error('District is required');
        isValid = false;
      } else if (!filterValues.village) {
        toast.error('City / Village is required');
        isValid = false;
      }
    }
    onSubmitFilter(isValid);
  };

  const colSize: ColXsMdType = {
    xs: 6,
    md: 3,
  };

  return (
    <div className=" border rounded ps-3 pt-2 pb-3 pe-3 bg-white">
      <Row>
        {listType === listTypes.review && (
          <Col xs={6} md={3}>
            <MTextField
              label="Sales Id"
              value={filterValues.activityId ?? ''}
              onChange={(e) => {
                setFilterValues((prev: any) => ({
                  ...prev,
                  activityId: e.target.value,
                }));
              }}
            />
          </Col>
        )}
        <Col xs={6} md={3}>
          <MSelect
            required={isCreateMode || isUpdateMode}
            label="Year"
            options={yearOptionsToCreate}
            value={filterValues?.year || null}
            onChange={(e) => {
              setFilterValues((prev: any) => ({
                ...prev,
                year: e.target.value,
              }));
            }}
            dataTestId={'year'}
            disabled={isUpdateMode}
          />
        </Col>
        <Col xs={6} md={3}>
          <MSelect
            required={isCreateMode || isUpdateMode}
            label="Season"
            options={seasonOptions ?? []}
            value={filterValues?.season || null}
            onChange={(e) => {
              setFilterValues((prev: any) => ({
                ...prev,
                season: e.target.value,
              }));
            }}
            dataTestId={'season'}
            disabled={isUpdateMode}
          />
        </Col>
        {listType === listTypes.review && (
          <Col xs={6} md={3}>
            <MSelect
              label="Status"
              options={statusOptions ?? []}
              value={filterValues?.status || null}
              onChange={(e) => {
                setFilterValues((prev: any) => ({
                  ...prev,
                  status: e.target.value,
                }));
              }}
              dataTestId={'status'}
            />
          </Col>
        )}
      </Row>
      <Row className="mt-3">
        <Col sx={6} md={isCreateMode || isUpdateMode ? 10 : 12}>
          <MUILocationFilter
            size={{
              zone: colSize,
              state: colSize,
              district: colSize,
              city: colSize,
            }}
            filterData={{
              zone: filterValues.zone,
              state: filterValues.state,
              district: filterValues.district,
              city: filterValues.village,
            }}
            setLocationFilter={(filter: MUILocationFilterType) => {
              setFilterValues({
                ...filterValues,
                zone: filter.zone,
                state: filter.state,
                district: filter.district,
                village: filter.city,
                pincode: filter.pincode ?? '',
              });
            }}
            required={isCreateMode || isUpdateMode}
            labels={{ city: 'City / Village' }}
            dataTestIds={{
              zone: 'zone',
              state: 'state',
              district: 'district',
              city: 'village',
            }}
            includeAllOption={listType === listTypes.review}
            defaultLocationOptions={defaultLocationOptions}
            disabled={isUpdateMode}
          />
        </Col>
        {(isCreateMode || isUpdateMode) && (
          <Col sx={6} md={2}>
            <MTextField
              label="Pincode"
              value={filterValues.pincode ?? ''}
              disabled
            />
          </Col>
        )}
      </Row>
      {!isUpdateMode && (
        <Col xs={6} md={4}>
          <div className="d-flex justify-content-left mt-4 pt-1">
            <Button
              variant="primary"
              className="cl-btn-primary"
              onClick={onApply}
            >
              {isCreateMode ? 'Submit' : 'Apply'}
            </Button>
            <Button
              variant="outline-primary"
              className="mx-2 cl-btn-outline-primary"
              onClick={onReset}
            >
              Reset
            </Button>
          </div>
        </Col>
      )}
    </div>
  );
};

export default SetActivityFilter;
